import {
  Component,
  computed,
  inject,
  Injector,
  input,
  InputSignal,
  Signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  IConfiguration,
  ITariffServiceAreaWithCredentialType,
} from '@web/dtos';
import { ToggleChipComponent } from '../../../../components/data-display/toggle-chip/toggle-chip.component';
import { DropDownComponent } from '../../../../components/data-input/form-inputs/drop-down/drop-down.component';
import { ListInputComponent } from '../../../../components/data-input/form-inputs/list-input/list-input.component';
import { MultiSelectDropDownComponent } from '../../../../components/data-input/form-inputs/multi-select-drop-down/multi-select-drop-down.component';
import { ConfigurationControlValue } from '../../../../entities/configuration-entity';
import {
  ConfigurationCustomInputService,
  EConfigurationCustomInput,
} from '../../../../services/api/configuration/configuration-custom-input.service';
import { LayoutsStore } from '../../../../services/api/layouts/layouts-store.service';
import { ServiceAreaStore } from '../../../../services/api/service-area/service-area.store';
import { TariffAreaStore } from '../../../../services/api/tariff-area/tariff-area-store.service';
import { OnDemandJourneyFormComponent } from '../configuration-on-demand-journey-form/configuration-on-demand-journey-form.component';

@Component({
  selector: 'aw-configuration-custom-input',
  imports: [
    DropDownComponent,
    ListInputComponent,
    MultiSelectDropDownComponent,
    OnDemandJourneyFormComponent,
    ReactiveFormsModule,
    ToggleChipComponent,
  ],
  templateUrl: './configuration-custom-input.component.html',
})
export class ConfigurationCustomInputComponent {
  private readonly injector: Injector = inject(Injector);

  readonly setting: InputSignal<IConfiguration> =
    input.required<IConfiguration>();
  readonly control: InputSignal<FormControl<ConfigurationControlValue>> =
    input.required<FormControl<ConfigurationControlValue>>();
  readonly inputId: InputSignal<string> = input.required<string>();

  protected readonly customInput: typeof EConfigurationCustomInput =
    EConfigurationCustomInput;
  private readonly customInputService: ConfigurationCustomInputService = inject(
    ConfigurationCustomInputService
  );
  protected readonly customInputType: Signal<EConfigurationCustomInput> =
    computed(
      (): EConfigurationCustomInput =>
        this.customInputService.customInputType(this.setting())
    );
  protected get serviceAreaStore(): ServiceAreaStore {
    return this.injector.get(ServiceAreaStore);
  }

  protected get tariffAreaStore(): TariffAreaStore {
    return this.injector.get(TariffAreaStore);
  }

  protected get layoutsStore(): LayoutsStore {
    return this.injector.get(LayoutsStore);
  }

  protected isTariffAreaSelected(
    tariffArea: ITariffServiceAreaWithCredentialType
  ): boolean {
    const value: ConfigurationControlValue = this.control().value;
    return Array.isArray(value) && value.includes(tariffArea.id);
  }

  protected toggleTariffArea(
    tariffArea: ITariffServiceAreaWithCredentialType
  ): void {
    const control: FormControl<ConfigurationControlValue> = this.control();
    if (control.disabled) {
      return;
    }

    const currentValue: Array<number | string> = Array.isArray(control.value)
      ? control.value
      : [];
    const updatedValue: Array<number | string> = currentValue.includes(
      tariffArea.id
    )
      ? currentValue.filter(
          (value: number | string): boolean => value !== tariffArea.id
        )
      : [...currentValue, tariffArea.id];

    control.setValue(updatedValue);
    control.markAsTouched();
    control.markAsDirty();
    control.updateValueAndValidity();
  }
}
